import {target} from '@github/catalyst'
import {getCookie} from '@github-ui/cookies'
import type {ErrorContext} from '@github-ui/failbot'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import ReactProfilingMode from '@github-ui/react-profiling-mode'
import {sendStats} from '@github-ui/stats'
import {type PropsWithChildren, StrictMode, version} from 'react'
import type {createRoot as createRootType, hydrateRoot as hydrateRootType, Root} from 'react-dom/client'
import {createRoot, hydrateRoot} from 'react-dom/client'

import {EXPECTED_ERRORS} from './expected-errors'
import {isDevelopmentOrStaffUser} from './is-development-or-is-staff-user'

type ReactDOM = {
  createRoot: typeof createRootType
  hydrateRoot: typeof hydrateRootType
}

const REACT_INVARIANT_ERROR_REGEX = /Minified React error #(?<invariant>\d+)/
const EXPECTED_INVARIANTS = [
  '419', // See https://react.dev/errors/419
  '421', // See https://react.dev/errors/421
]

export abstract class ReactBaseElement<T> extends HTMLElement {
  @target declare embeddedData: HTMLScriptElement | undefined
  @target declare ssrError: HTMLScriptElement | undefined
  @target declare reactRoot: HTMLElement | undefined
  #root: Root | undefined
  #styledComponentsTags: HTMLStyleElement[] | undefined
  #styledComponentsRootId: string | undefined
  declare embeddedDataJSON: T

  abstract nameAttribute: string
  abstract getReactNode(
    embeddedData: T,
    onError: (error: Error, context?: ErrorContext) => void,
  ): Promise<React.ReactNode>

  protected get name() {
    return this.getAttribute(this.nameAttribute) as string
  }

  get #embeddedDataText() {
    const text = this.embeddedData?.textContent
    if (!text) {
      throw new Error(`No embedded data provided for react element ${this.name}`)
    }

    return text
  }

  get hasSSRContent() {
    return this.getAttribute('data-ssr') === 'true'
  }

  get attemptedSSR() {
    return this.getAttribute('data-attempted-ssr') === 'true'
  }

  connectedCallback() {
    this.embeddedDataJSON = JSON.parse(this.#embeddedDataText) as T
    this.#renderReact()
  }

  disconnectedCallback() {
    this.#root?.unmount()
    this.#root = undefined

    // Clean up our styled-components SSR tags
    // These are only set when deferred cleanup was enabled, so their presence is the marker
    if (this.#styledComponentsTags && this.#styledComponentsRootId) {
      const tagsToClean = this.#styledComponentsTags
      const rootId = this.#styledComponentsRootId
      requestIdleCallback(() => {
        for (const tag of tagsToClean) {
          // Only remove if:
          // 1. Still in DOM
          // 2. Still marked as SSR tag (not converted to active by styled-components)
          // 3. Still has OUR root ID
          if (
            tag.parentElement &&
            tag.getAttribute('data-styled') === 'true' &&
            tag.getAttribute('data-react-root-id') === rootId
          ) {
            tag.remove()
          }
        }
      })
      this.#styledComponentsTags = undefined
      this.#styledComponentsRootId = undefined
    }
  }

  /**
   * A React component to wrap the `baseNode` in with a custom display name to distinguish the app/partial and element. This is primarily used for devtools.
   * It's ok to create a component dynamically here, since we only render the application once.
   */
  #createWrapperForReactRoot() {
    const Application = ({children}: PropsWithChildren) => {
      return children
    }

    Application.displayName = `${this.tagName}(${this.name})`
    return Application
  }

  #profilingModeEnabled() {
    return ReactProfilingMode.isEnabled() || this.getAttribute('data-react-profiling') === 'true'
  }

  #takeMeasure(entryName: string, startMarkName: string, endMarkName: string) {
    try {
      window.performance.mark(endMarkName)
      /**
       * You need a microtask (like queueMicrotask) in performance measurement to ensure all performance marks have been registered before calling performance.measure()—especially in high-speed synchronous code paths.
       * Otherwise it's possible that the marks won't be registered in time, leading to SyntaxError reading a mark that isn't available yet.
       */
      queueMicrotask(() => {
        try {
          if (
            window.performance.getEntriesByName(startMarkName).length === 1 &&
            window.performance.getEntriesByName(endMarkName).length === 1
          ) {
            const entry = window.performance.measure(entryName, startMarkName, endMarkName)
            sendStats({
              reactHydrationTimings: {
                duration: entry.duration,
                appName: this.name,
                reactVersion: version,
                renderType: this.hasSSRContent ? 'hydrateRoot' : 'createRoot',
              },
              requestUrl: location.href,
            })
          }
        } catch {
          // silently fail
        } finally {
          window.performance.clearMarks(startMarkName)
          window.performance.clearMarks(endMarkName)
          window.performance.clearMeasures(entryName)
        }
      })
    } catch {
      // silently fail
    }
  }
  /**
   * @returns A function that, when called, will end the performance mark and emit stats call to track the initial render duration.
   */
  #startInitialRenderPerformanceMeasure(): () => void {
    try {
      const uuid = crypto.randomUUID()
      const baseName = `react-base-element-initial-render-${this.name}-[${uuid}]`
      const startMark = `${baseName}-start`
      const endMark = `${baseName}-end`
      const entryName = `${baseName}-entry`

      window.performance.mark(startMark)

      return () => {
        this.#takeMeasure(entryName, startMark, endMark)
      }
    } catch {
      // silently fail
      return noop
    }
  }

  async #renderReact() {
    if (!this.reactRoot) throw new Error('No react root provided')
    let reactDom: ReactDOM = {
      createRoot,
      hydrateRoot,
    }
    // Override the react-dom import if we're in profiling mode
    if (this.#profilingModeEnabled()) {
      reactDom = await this.#getReactDomWithProfiling()
    }

    const collectRenderPerformanceStats = this.#startInitialRenderPerformanceMeasure()

    let hydrationError = false
    const onError = (error: Error, context: ErrorContext = {}) => {
      hydrationError = true

      const ctx = {
        critical: true,
        reactAppName: this.name,
        ...context,
      }
      setTimeout(() => {
        reportError(error, ctx)
      })
    }
    const embeddedData = this.embeddedDataJSON
    const ssrErrorText = this.ssrError?.textContent
    const node = await this.getReactNode(embeddedData, onError)

    const Application = this.#createWrapperForReactRoot()

    const baseNode = (
      <StrictMode>
        <Application>{node}</Application>
      </StrictMode>
    )

    if (ssrErrorText) {
      this.#logSSRError(ssrErrorText)
    }

    if (this.hasSSRContent) {
      const deferStyleTagCleanup = isFeatureEnabled('react_defer_ssr_style_tag_cleanup')

      /**
       * Styled-components automatically looks for style tags to hydrate on first page load, but will not detect them
       * if they are added after the initial page load. This causes a hydration error because React isn't expecting
       * a style tag within the app. To work around this, we need to manually move the style tags to the head before
       * hydrating the app.
       *
       * When deferStyleTagCleanup is enabled, we mark styled-components tags with a unique root ID so that:
       * - Multiple roots can coexist without interfering with each other's styles
       * - Styled-components can rehydrate from SSR tags across Turbo navigations
       * - Tags are only cleaned up when their specific root disconnects
       */
      const styledComponentsTags = [...this.querySelectorAll<HTMLStyleElement>('style[data-styled="true"]')]

      /**
       * When using Vite, we inject temporary <link> tags into the SSRd content to provide CSS Module styles on page
       * load. These tags can be removed after hydration, but need to be moved to the <head> initially to avoid
       * hydration errors. After hydration, the JS version of these CSS modules will be loaded, providing the same
       * styles with HMR support.
       */
      const viteTemporaryTags = [...this.querySelectorAll<HTMLLinkElement>('link[data-remove-after-hydration="true"]')]

      if (deferStyleTagCleanup) {
        // New behavior: Mark styled-components tags with a unique ID for THIS root
        // and defer cleanup to disconnectedCallback
        const rootId = crypto.randomUUID()
        for (const tag of styledComponentsTags) {
          tag.setAttribute('data-react-root-id', rootId)
          document.head.appendChild(tag)
        }

        // Store for cleanup on disconnect
        this.#styledComponentsTags = styledComponentsTags
        this.#styledComponentsRootId = rootId
      } else {
        // Old behavior: Move styled-components tags to head (will be removed after hydration)
        for (const tag of styledComponentsTags) {
          document.head.appendChild(tag)
        }
      }

      // Move Vite tags to head (these will be removed after hydration)
      for (const tag of viteTemporaryTags) {
        document.head.appendChild(tag)
      }

      // Hydrate the react app
      // onRecoverableError is disabled until we have a react version with this fix in:
      // https://github.com/facebook/react/pull/25692
      this.#root = reactDom.hydrateRoot(this.reactRoot, baseNode, {
        onRecoverableError: (error, errorInfo) => {
          if (!(error instanceof Error)) return

          const match = REACT_INVARIANT_ERROR_REGEX.exec(error.message)
          const invariant = String(match?.groups?.invariant)
          hydrationError = !EXPECTED_INVARIANTS.includes(invariant)
          sendStats({
            incrementKey: 'REACT_HYDRATION_ERROR',
            incrementTags: {
              appName: this.name,
              invariant,
            },
            requestUrl: window.location.href,
          })

          /** Log unexpected hydration errors in development or production for a staff user */
          if (!hydrationError || !isDevelopmentOrStaffUser()) return
          // eslint-disable-next-line no-console
          console.groupCollapsed(
            `%c⚠️ Recoverable hydration error - ${this.name} - ${error.message}`,
            'background: rgba(255, 193, 7, 0.2); font-weight: bold; padding: 4px; border: 1px solid rgba(255, 193, 7, 0.5); border-radius: 4px;',
            'This is only visible to staff users and is safe to ignore. Reach out to #react for help understanding and fixing these hydration errors',
          )
          if (process.env.NODE_ENV === 'development') {
            const isHydrationScanningEnabled = getCookie('hydrationScan')?.value !== 'false'
            if (isHydrationScanningEnabled) {
              const errorContainer = document.querySelector<HTMLElement>('#hydration-error-notice')!
              const errorCounter = errorContainer.querySelector<HTMLElement>('.js-hydration-error-count')!
              errorCounter.textContent = (parseInt(errorCounter.textContent || '0', 10) + 1).toString()
              errorContainer.hidden = false
            }
          }
          if (error.cause) {
            // eslint-disable-next-line no-console
            console.warn('cause', error.cause)
          }
          if (errorInfo.componentStack) {
            // eslint-disable-next-line no-console
            console.warn('componentStack', errorInfo.componentStack)
          }
          // eslint-disable-next-line no-console
          console.groupEnd()
        },
      })

      // Remove the manually moved style tags after hydration
      // When deferStyleTagCleanup is enabled, styled-components tags are cleaned up in disconnectedCallback instead
      const tagsToRemoveAfterHydration = deferStyleTagCleanup
        ? viteTemporaryTags
        : [...styledComponentsTags, ...viteTemporaryTags]

      if (tagsToRemoveAfterHydration.length > 0) {
        // Wait until things are idle to remove the style tag. If we do it immediately, we can cause a flash of unstyled content.
        requestIdleCallback(() => {
          // styles could have already been removed by Turbo if a navigation happens quickly. Only remove it from the DOM if it's still there.
          for (const tag of tagsToRemoveAfterHydration) {
            tag.parentElement?.removeChild(tag)
          }
        })
      }

      sendStats({
        incrementKey: 'REACT_RENDER',
        incrementTags: {
          appName: this.name,
          csr: false,
          error: hydrationError,
          ssr: true,
          ssrError: false,
        },
      })
    } else {
      this.#root = reactDom.createRoot(this.reactRoot)
      this.#root.render(baseNode)

      sendStats({
        incrementKey: 'REACT_RENDER',
        incrementTags: {
          appName: this.name,
          csr: true,
          error: hydrationError,
          ssr: this.attemptedSSR,
          ssrError: !!this.ssrError,
        },
      })
    }

    this.classList.add('loaded')
    collectRenderPerformanceStats()
  }

  #getReactDomWithProfiling() {
    return import('react-dom/profiling') as unknown as Promise<ReactDOM>
  }

  #logSSRError(ssrErrorText: string) {
    if (!isDevelopmentOrStaffUser()) return
    if (EXPECTED_ERRORS[ssrErrorText]) {
      // eslint-disable-next-line no-console
      return console.error('SSR failed with an expected error:', EXPECTED_ERRORS[ssrErrorText])
    }

    try {
      const error = JSON.parse(ssrErrorText) as PlatformJavascriptError
      const stacktrace = parseFailbotStacktrace(error)
      // eslint-disable-next-line no-console
      console.error(
        'Error During Alloy SSR:',
        `${this.tagName.toLowerCase()}[${this.name}]`,
        `${error.type}: ${error.value}\n`,
        error,
        stacktrace,
      )
    } catch {
      /**
       * In the event we couldn't log the error, we should not break the application
       */
      // eslint-disable-next-line no-console
      console.error(
        'Error During Alloy SSR:',
        `${this.tagName.toLowerCase()}[${this.name}]`,
        ssrErrorText,
        'unable to parse as json',
      )
    }
  }
}

function parseFailbotStacktrace(error: PlatformJavascriptError) {
  if (!error.stacktrace) return ''
  let prefix = '\n '
  const stack = error.stacktrace.map((frame: PlatformStackframe) => {
    const {function: func, filename, lineno, colno} = frame
    const line = `${prefix} at ${func} (${filename}:${lineno}:${colno})`
    prefix = ' '
    return line
  })
  return stack.join('\n')
}

try{ REACT_INVARIANT_ERROR_REGEX.displayName ||= 'REACT_INVARIANT_ERROR_REGEX' } catch {}